/**
 * 用户管理Store (Pinia)
 * 负责用户认证、用户信息管理、套餐管理等
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { authApi } from '@/api/auth';
import { generateBrowserFingerprint } from '@/utils/app-runtime';
import router from '@/router';
// ApiResponse 类型暂时移除
import type { User, UserPreferences } from '@/types/userTypes';
import type { UserPackage } from '@/api/package';
import { DEFAULT_USER_PREFERENCES } from '@/types/userTypes';

// errorManager 暂时移除

// 定义用户使用状态响应类型
interface UserUsageStatusResponse {
  success: boolean;
  data?: any;
  message?: string;
}

export const useUserStore = defineStore('user', () => {
  // ============= 状态定义 =============
  const currentUser = ref<User | null>(null);
  const isLoading = ref(false);
  const isInitialized = ref(false);
  const isAuthReady = ref(false); // 新增：认证就绪状态
  const error = ref<string | null>(null);
  
  // 用户套餐信息
  const userPackage = ref<UserPackage | null>(null);
  const packageLoading = ref(false);
  const packageLoaded = ref(false); // 标记是否已加载过套餐信息
  
  // 用户偏好设置
  const preferences = ref<UserPreferences>(DEFAULT_USER_PREFERENCES);
  
  // 用户使用统计
  const usageStats = ref({
    totalCharacters: 0,
    remainingCharacters: 0,
    totalTasks: 0,
    successfulTasks: 0
  });

  // ✅ 新增：用户使用限制信息
  const userLimits = ref<{
    used: number;
    total: number;
    remaining: number;
  } | null>(null);

  // ============= 计算属性 =============
  const isLoggedIn = computed(() => {
    return currentUser.value !== null;
  });

  // 内存中的token状态，避免直接读localStorage
  const authToken = ref<string | null>(null);

  const token = computed(() => {
    return authToken.value;
  });

  const isVip = computed(() => {
    return userPackage.value?.package?.versionCode !== 'free';
  });
  
  const hasValidPackage = computed(() => {
    if (!userPackage.value) return false;
    const now = new Date();
    const expireDate = new Date(userPackage.value.expireTime);
    return userPackage.value.status === 'active' && expireDate > now;
  });
  
  const remainingChars = computed(() => {
    return userPackage.value?.credits || 0;
  });

  // ============= Actions =============
  
  /**
   * 用户登录
   */
  const login = async (credentials: { email: string; password: string }) => {
    try {
      isLoading.value = true;
      error.value = null;
      
      const responseData = await authApi.login(credentials);

      // authApi 已使用新SDK，直接返回业务数据
      currentUser.value = (responseData as any).user || responseData;

      // 保存token到内存和本地存储
      const tokenValue = (responseData as any).token || 'mock_token';
      authToken.value = tokenValue;
      localStorage.setItem('auth_token', tokenValue);
      localStorage.setItem('user_info', JSON.stringify(currentUser.value));

      // 并行获取用户套餐信息和权益信息
      const { useEntitlementStore } = await import('./entitlement');
      const entitlementStore = useEntitlementStore();

      await Promise.all([
        fetchUserPackage(),
        entitlementStore.fetchAll()
      ]);

      ElMessage.success('登录成功');
      return responseData;
    } catch (err: any) {
      error.value = err.message || '登录失败';
      ElMessage({ type: 'error', message: error.value || '登录失败' });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * 用户注册
   */
  const register = async (userData: {
    username: string;
    email: string;
    password: string;
    verificationCode: string;
  }) => {
    try {
      isLoading.value = true;
      error.value = null;

      const responseData = await authApi.register(userData);

      // authApi 已使用新SDK，直接返回业务数据
      // 注意：成功消息由调用方（LoginDialog）显示，这里不再重复显示
      return responseData;
    } catch (err: any) {
      error.value = err.message || '注册失败';
      ElMessage({ type: 'error', message: error.value || '注册失败' });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 发送邮箱验证码
   */
  const sendVerificationCode = async (email: string) => {
    try {
      isLoading.value = true;
      error.value = null;

      const responseData = await authApi.sendVerificationCode(email);

      // authApi 已使用新SDK，直接返回业务数据
      ElMessage({ type: 'success', message: '验证码已发送，请查收邮箱' });
      return responseData;
    } catch (err: any) {
      error.value = err.message || '发送验证码失败';
      ElMessage({ type: 'error', message: error.value || '发送验证码失败' });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 处理微信登录成功后的状态更新
   */
  const handleWechatLoginSuccess = async (data: { token: string; userInfo: any }) => {
    try {
      // 更新用户信息
      currentUser.value = data.userInfo;

      // 保存token到内存和本地存储
      authToken.value = data.token;
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_info', JSON.stringify(data.userInfo));

      // 等待下一个 tick，确保 isLoggedIn 计算属性已更新
      await new Promise(resolve => setTimeout(resolve, 0));

      // 并行获取用户套餐信息和权益信息
      const { useEntitlementStore } = await import('./entitlement');
      const entitlementStore = useEntitlementStore();

      await Promise.all([
        fetchUserPackage(),
        entitlementStore.fetchAll()
      ]);
    } catch (err: any) {
      console.error('微信登录后数据刷新失败:', err);
      throw err;
    }
  };

  /**
   * 用户登出
   */
  const logout = async () => {
    try {
      // 调用登出API，清除服务器端的 HttpOnly Cookie
      const { authApi } = await import('@/api/auth');
      await authApi.logout();
    } catch (err) {
      console.warn('登出API调用失败:', err);
    } finally {
      // 清除本地状态
      currentUser.value = null;
      userPackage.value = null;
      packageLoaded.value = false; // 重置加载标记
      preferences.value = DEFAULT_USER_PREFERENCES;

      // 清除内存和本地存储
      authToken.value = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_info');

      // 重置权益信息（配额、套餐等）
      const { useEntitlementStore } = await import('./entitlement');
      const entitlementStore = useEntitlementStore();
      entitlementStore.reset();

      // 重新获取游客配额
      await entitlementStore.fetchAll();

      // 重置成就信息
      const { useAchievementStore } = await import('./achievement');
      const achievementStore = useAchievementStore();
      achievementStore.clearData();

      // 跳转到登录页
      router.push('/auth/login');

      ElMessage.success('已退出登录');
    }
  };
  
  /**
   * 获取用户套餐信息
   */
  const fetchUserPackage = async (force = false) => {
    if (!isLoggedIn.value) return;

    // 如果已加载过且不强制刷新,直接返回 (无论是否有套餐)
    if (packageLoaded.value && !force) {
      return;
    }

    try {
      packageLoading.value = true;
      const { packageApi } = await import('@/api/package');
      const response = await packageApi.getCurrentPackage();

      // 检查返回数据是否有效
      if (!response || !response.package) {
        console.warn('用户暂无套餐信息');
        userPackage.value = null;
        packageLoaded.value = true; // 标记已加载,即使没有套餐
        return;
      }

      // 转换字段名：expire_time -> expireTime
      userPackage.value = {
        package: response.package,
        expireTime: response.expire_time,
        credits: response.credits,
        status: 'active' // 默认状态，后续可根据实际情况调整
      };
      packageLoaded.value = true; // 标记已加载
    } catch (err: any) {
      console.error('获取用户套餐信息失败:', err);
      userPackage.value = null;
      packageLoaded.value = true; // 即使失败也标记已加载,避免重复请求
    } finally {
      packageLoading.value = false;
    }
  };
  
  /**
   * 更新用户信息
   */
  const updateUserInfo = async (updates: Partial<User>) => {
    if (!currentUser.value) return;

    try {
      isLoading.value = true;

      // 调用后端API更新用户信息
      const { userApi } = await import('@/api/user');
      const response = await userApi.updateUserInfo({
        username: updates.username,
        phone: updates.phone,
        introduction: updates.introduction
      });

      // 更新内存中的用户信息
      currentUser.value = { ...currentUser.value, ...response };

      // 更新本地存储
      localStorage.setItem('user_info', JSON.stringify(currentUser.value));
      
    } catch (err: any) {
      error.value = err.message || '更新用户信息失败';
      ElMessage({ type: 'error', message: error.value || '更新用户信息失败' });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 设置/修改密码
   */
  const setPassword = async (password: string, confirmPassword: string, oldPassword?: string) => {
    if (!currentUser.value) return;

    try {
      isLoading.value = true;

      // 调用后端API设置密码
      const { userApi } = await import('@/api/user');
      await userApi.setPassword({
        oldPassword,
        password,
        confirmPassword
      });

      ElMessage.success(oldPassword ? '密码修改成功' : '密码设置成功');
    } catch (err: any) {
      error.value = err.message || '设置密码失败';
      ElMessage.error(error.value || '设置密码失败');
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 更新用户偏好设置
   */
  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    preferences.value = { ...preferences.value, ...newPreferences };
    
    // 保存到本地存储
    localStorage.setItem('user_preferences', JSON.stringify(preferences.value));
  };
  
  /**
   * 从本地存储恢复用户状态
   */
  const restoreUserState = () => {
    const token = localStorage.getItem('auth_token');
    const userInfo = localStorage.getItem('user_info');
    const savedPreferences = localStorage.getItem('user_preferences');

    if (token && userInfo) {
      try {
        // 恢复token到内存
        authToken.value = token;
        currentUser.value = JSON.parse(userInfo);
      } catch (err) {
        console.error('恢复用户状态失败:', err);
        logout();
      }
    }

    if (savedPreferences) {
      try {
        preferences.value = { ...preferences.value, ...JSON.parse(savedPreferences) };
      } catch (err) {
        console.error('恢复用户偏好设置失败:', err);
      }
    }

    // 标记认证状态已就绪
    isAuthReady.value = true;
  };
  
  /**
   * 检查用户使用状态
   */
  const checkUsageStatus = async (): Promise<UserUsageStatusResponse> => {
    if (!isLoggedIn.value) {
      return { success: false, message: '用户未登录' };
    }
    
    try {
      // 这里应该调用检查用户使用状态的API
      // const response = await userApi.getUsageStatus();
      // usageStats.value = response.data;
      
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err.message || '检查使用状态失败' };
    }
  };

  /**
   * 初始化用户状态
   */
  const initialize = async () => {
    if (isInitialized.value) {
      return; // 避免重复初始化
    }

    try {
      isLoading.value = true;

      // 预生成浏览器指纹，确保后续HTTP请求时已就绪
      generateBrowserFingerprint();

      // 首先恢复用户状态
      restoreUserState();

      // 注意：不在这里调用 fetchUserPackage()，改为按需加载
      // 套餐信息会在以下时机加载：
      // 1. 用户登录时（login方法中）
      // 2. 进入个人中心页面时（UserCenter.vue的onMounted中）

      isInitialized.value = true;
    } catch (error: any) {
      console.warn('用户状态初始化失败:', error.message);
      // 清除无效token
      authToken.value = null;
      localStorage.removeItem('auth_token');
      isInitialized.value = true; // 即使失败也标记为已初始化，避免无限等待
      isAuthReady.value = true; // 即使失败也标记认证就绪
    } finally {
      isLoading.value = false;
    }
  };

  // ============= 初始化 =============
  // 在store创建时恢复用户状态
  restoreUserState();

  return {
    // 状态
    currentUser,
    isLoading,
    isInitialized,
    error,
    userPackage,
    packageLoading,
    preferences,
    usageStats,
    userLimits, // ✅ 新增

    // 计算属性
    isLoggedIn,
    token,
    isVip,
    hasValidPackage,
    remainingChars,

    // 认证状态
    isAuthReady,

    // 方法
    initialize,
    login,
    register,
    sendVerificationCode,
    handleWechatLoginSuccess,
    logout,
    fetchUserPackage,
    updateUserInfo,
    setPassword,
    updatePreferences,
    restoreUserState,
    checkUsageStatus
  };
});
